// v1.1.0 - 2026-02-02 - Auth store with detailed logging

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, AuthState } from '@/types'
import type { CERSettings } from '@/lib/ti-calculator/types'

interface AuthStore extends AuthState {
  cerSettings: CERSettings | null
  cer: { id: string; name: string; crin: string } | null
  token: string | null

  // Actions
  setUser: (user: User | null) => void
  setToken: (token: string | null) => void
  setCERSettings: (settings: CERSettings | null) => void
  setLoading: (loading: boolean) => void
  logout: () => void

  // Async actions
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  requestMagicLink: (email: string) => Promise<{ success: boolean; error?: string }>
  verifySession: () => Promise<boolean>
}

const API_BASE = '/api'

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: true,
      cerSettings: null,
      cer: null,
      token: null,

      // Setters
      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      setToken: (token) => set({ token }),

      setCERSettings: (cerSettings) => set({ cerSettings }),

      setLoading: (isLoading) => set({ isLoading }),

      logout: () => {
        // Call logout API
        fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          credentials: 'include',
        }).catch(console.error)

        set({
          user: null,
          isAuthenticated: false,
          token: null,
          cerSettings: null,
        })
      },

      // Login with email/password
      login: async (email, password) => {
        try {
          console.log('[Auth] Starting login for:', email)
          const response = await fetch(`${API_BASE}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, password }),
          })

          console.log('[Auth] Response status:', response.status)
          const text = await response.text()
          console.log('[Auth] Response body:', text.slice(0, 500))

          let data
          try {
            data = JSON.parse(text)
          } catch (parseError) {
            console.error('[Auth] JSON parse error:', parseError)
            return { success: false, error: `Errore parsing risposta: ${text.slice(0, 100)}` }
          }

          if (!data.success) {
            console.log('[Auth] Login failed:', data.error)
            return { success: false, error: data.error || 'Errore sconosciuto dal server' }
          }

          console.log('[Auth] Login successful, user:', data.data.user.email)
          set({
            user: data.data.user,
            token: data.data.token,
            isAuthenticated: true,
          })

          // Fetch CER settings
          get().verifySession()

          return { success: true }
        } catch (error) {
          console.error('[Auth] Login exception:', error)
          return { success: false, error: `Errore di connessione: ${error instanceof Error ? error.message : String(error)}` }
        }
      },

      // Request magic link
      requestMagicLink: async (email) => {
        try {
          const response = await fetch(`${API_BASE}/auth/magic-link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          })

          const data = await response.json()

          if (!data.success) {
            return { success: false, error: data.error }
          }

          return { success: true }
        } catch (error) {
          console.error('Magic link error:', error)
          return { success: false, error: 'Errore di connessione' }
        }
      },

      // Verify current session
      verifySession: async () => {
        set({ isLoading: true })

        try {
          const response = await fetch(`${API_BASE}/auth/me`, {
            credentials: 'include',
          })

          if (!response.ok) {
            set({
              user: null,
              isAuthenticated: false,
              token: null,
              cerSettings: null,
              isLoading: false,
            })
            return false
          }

          const data = await response.json()

          if (data.success && data.data) {
            set({
              user: data.data.user,
              cerSettings: data.data.cer_settings,
              isAuthenticated: true,
              isLoading: false,
            })
            return true
          }

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          })
          return false
        } catch (error) {
          console.error('Session verify error:', error)
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          })
          return false
        }
      },
    }),
    {
      name: 'cer-auth',
      partialize: (state) => ({
        token: state.token,
      }),
    }
  )
)

export default useAuthStore
